import { ref, type Ref } from "vue";
import { until } from "@vueuse/core";
import { JobRepository } from "~/repositories/JobRepository";
import type { JobData } from "~/types/job";
import { useCompany, type CompanyItem } from "~/composables/useCompany";
import { jobExpiryFromApi } from "~/utils/jobDeadline";
import { LocalesString } from "~~/types";

// JobSalary típus definíciója
export type JobSalary =
	| {
			notSpecified: true;
	  }
	| {
			notSpecified: false;
			currency?: string;
			amountIn?: string;
			salaryPeriod?: string;
			salary?: string;
			other?: LocalesString;
			bonus?: LocalesString;
	  };

// A Job adatok típusa, most a 'company' egy Ref
export type JobItem = JobData & {
	company: CompanyItem | null;
	loadCompany: () => Promise<void>;
	jobTags: (string | Record<string, string>)[];
	jobSalary: JobSalary | null;
	remainingDays: number;
	expiredJob: boolean;
};

// Lookup slug by job slug in payload data, if not found return first available
export const getJobSlug = (job: JobData, locale: string): string => {
	const localizedSlug = job.slug?.[locale];
	if (localizedSlug) return localizedSlug;

	return "";
};

// --- A COMPOSABLE FUNKCIÓ ---

/**
 * A useJob composable lekéri egy adott állás (job) adatait az API-ból.
 * Tartalmaz egy lusta betöltési funkciót a kapcsolódó cég (company) adatainak lekéréséhez.
 *
 * @example
 * const { jobItem, loading, error } = useJob(jobIdOrSlug);
 *
 * // A cég adatainak lusta betöltése
 * await jobItem.value.loadCompany();
 * console.log(jobItem.value.company.value); // A cég adatai
 *
 */
export function useJob(jobIdOrSlug: number | string, locale: string) {
	const jobRepository = new JobRepository();
	const jobItem: Ref<JobItem | null> = ref(null);
	const loading = ref(true);
	const error = ref<Error | null>(null);

	// Job tags összegyűjtése
	const mergeJobTags = (data: JobData): (string | Record<string, string>)[] => {
		const jobTags: (string | Record<string, string>)[] = [];

		const neededTags = ["job_level", "job_type", "job_category"];
		neededTags.forEach((tag) => {
			if (data?.tags?.[tag]?.data?.length > 0) {
				if (["job_type"].includes(tag)) {
					data.tags[tag].data.forEach((type: { name: Record<string, string> }) => {
						jobTags.push(type.name);
					});
					return;
				}
			}
		});
		return jobTags;
	};
	const calculateJobSalary = (data: JobData): JobSalary | null => {
		if (!data.boxes?.job_salary?.data) return null;

		const salary = data.boxes.job_salary.data;

		if (salary.not_specified) {
			return {
				notSpecified: true as const,
			};
		}

		return {
			notSpecified: false as const,
			currency: salary.currency,
			amountIn: salary.amount_in,
			salaryPeriod: salary.salary_period ?? "month",
			other: salary.other,
			bonus: salary.bonus_description,
			...(salary.salary_from &&
				salary.salary_to && {
					salary:
						salary.salary_to == salary.salary_from
							? Intl.NumberFormat("hu-HU", {}).format(Number.parseInt(salary.salary_from)).toString()
							: `${Intl.NumberFormat("hu-HU", {}).format(
									Number.parseInt(salary.salary_from)
							  )} - ${Intl.NumberFormat("hu-HU", {}).format(Number.parseInt(salary.salary_to))}`,
				}),
		};
	};

	// A fő Job lekérő logika
	const fetchJob = async (idOrSlug: number | string, locale: string) => {
		loading.value = true;
		error.value = null;
		try {
			const { data, error: fetchError } = await jobRepository.getJob(idOrSlug, locale);

			if (fetchError) {
				throw fetchError;
			}
			if (!data) {
				throw new Error("Job not found.");
			}

			const companyData: CompanyItem | null = null; // NEM ref!
			let companyFetched = false;

			// Lazy loading függvény a cég adatainak betöltéséhez
			const loadCompany = async () => {
				if (companyFetched || !data.company_id) return;
				companyFetched = true;

				const { companyItem, loading: companyLoading, error: companyError } = useCompany(data.company_id);

				await until(companyLoading).toBe(false);

				if (companyError.value) {
					console.error("Company fetch error:", companyError.value);
					return;
				}

				// A teljes jobItem objektumot újra kell rendelni a reaktivitáshoz
				if (jobItem.value) {
					jobItem.value = {
						...jobItem.value,
						company: companyItem.value,
					};
				}
			};

			// Fill missing locale-specific slugs with default slug
			const availableLocales = useRegion.getCurrentRegion().availableLangs; // Add all your supported locales
			availableLocales.forEach((locale) => {
				data.slug[locale] = getJobSlug(data, locale);
			});

			// Job tags összegyűjtése
			const jobTags = mergeJobTags(data);
			const jobSalary = calculateJobSalary(data);
			const { remainingDays, expiredJob } = jobExpiryFromApi(data);

			// Összeállítjuk a végső reaktív objektumot
			const finalJobItem: JobItem = {
				...data,
				company: companyData,
				loadCompany,
				jobTags,
				jobSalary,
				remainingDays,
				expiredJob,
			};

			jobItem.value = finalJobItem;
		} catch (err) {
			error.value = err instanceof Error ? err : new Error(String(err)); // TODO FetchError
			jobItem.value = null;
			loading.value = false;
		} finally {
			loading.value = false;
		}
	};

	fetchJob(jobIdOrSlug, locale);

	return { jobItem, loading, error };
}
